import type { CompletedInteraction } from '@dhl/receiver-profile-api'
import { links } from '~/links.js'
import { stepNamePrefix, steps } from './steps.js'
import { useGetProfile } from '../../profile/model/profile/hooks.js'

type Args = {
  initialSetupRequired: boolean
  enabledDate?: string
}

type GuardHookResult = {
  isLoading: boolean
  data: {
    isCompleted: boolean
    linkToRequirement?: string
  }
}

export type OnboardingCompletedGuardResult = {
  isLoading: boolean
  data: string | undefined
}

const isSetupRequiredForDate = (requiredFromDateString?: string, dateInput?: string): boolean => {
  if (!requiredFromDateString || !dateInput) {
    return false
  }

  const requiredFromDate = Date.parse(requiredFromDateString)

  return Date.parse(dateInput) >= requiredFromDate
}

export const useGetLastCompletedProfileSetupStep = (): ReturnType<
  typeof useGetProfile<CompletedInteraction, unknown>
> =>
  useGetProfile({
    query: {
      select: (data) => {
        const [lastCompletedStep] = (data?.completedInteractions ?? [])
          .filter((t) => t.name.startsWith(stepNamePrefix))
          .sort((a, b) => a.completedAt.localeCompare(b.completedAt))
          .reverse()

        return lastCompletedStep
      },
    },
  })

const useIsProfileSetupCompleted = (enabledDate?: string): GuardHookResult => {
  const { data: profileSetupCompleted, isLoading } = useGetProfile({
    query: {
      select: (data) => data?.completedInteractions?.some(({ name }) => name === steps.done) ?? false,
    },
  })

  const { data: profile } = useGetProfile()
  const profileSetupLink = links.profileSetupLink()
  const profileSetupRequired = isSetupRequiredForDate(enabledDate, profile?.createdAt)

  if (!profileSetupRequired) {
    return {
      isLoading,
      data: {
        isCompleted: true,
      },
    }
  }

  return {
    isLoading,
    data: {
      isCompleted: profileSetupCompleted ?? false,
      linkToRequirement: profileSetupLink,
    },
  }
}

const useHasFullNameInProfile = (): GuardHookResult => {
  const { data: profile, isLoading } = useGetProfile()
  const profileSetupNameLink = links.profileSetupNameLink()

  return {
    isLoading,
    data: {
      isCompleted: Boolean(profile?.firstName && profile.lastName),
      linkToRequirement: profileSetupNameLink,
    },
  }
}

export const useProfileOnboardingCompletedGuard = ({
  enabledDate,
  initialSetupRequired,
}: Args): OnboardingCompletedGuardResult => {
  const { data: fullNameGuard, isLoading: isLoadingFullNameInProfile } = useHasFullNameInProfile()
  const { data: profileSetupGuard, isLoading: isLoadingProfileSetupCompleted } = useIsProfileSetupCompleted(enabledDate)

  if (!initialSetupRequired) {
    return {
      isLoading: false,
      data: undefined,
    }
  }

  const requirements = [fullNameGuard, profileSetupGuard]
  const [link] = requirements
    .filter(({ isCompleted }) => !isCompleted)
    .map(({ linkToRequirement }) => linkToRequirement)

  return {
    isLoading: isLoadingFullNameInProfile || isLoadingProfileSetupCompleted,
    data: link,
  }
}
